<script setup lang="ts">
/**
 * Title band at the top of every inner page (light surface, dark in dark mode): page title,
 * an optional one-line lead and the visible breadcrumb trail. Home is always
 * the first crumb; the last item is the current page and not a link.
 *
 * Visual only. Pages keep emitting BreadcrumbList JSON-LD through useJsonLd so
 * the two are built from the same data.
 */
export interface SofaxCrumb {
  name: string
  /** Omit on the last item, the current page. */
  to?: string
}

withDefaults(defineProps<{
  title: string
  subtitle?: string
  items?: SofaxCrumb[]
  /** Renders the title as <h1> (page) or <div> when the page has its own h1. */
  as?: 'h1' | 'div'
}>(), {
  items: () => [],
  as: 'h1',
})

const { t } = useI18n()
</script>

<template>
  <section class="sofax relative overflow-hidden border-b border-line bg-surface dark:border-gray-800 dark:bg-gray-900">
    <div class="breadcrumb-space">
      <div class="shell relative z-10 text-center">
        <component
          :is="as"
          class="display-heading mx-auto mb-6 max-w-5xl !text-4xl leading-[1.05] !text-ink sm:!text-5xl md:mb-8 md:!text-6xl xl:!text-7xl dark:!text-white"
        >
          {{ title }}
        </component>

        <p v-if="subtitle" class="mx-auto mb-8 max-w-3xl text-lg !text-body md:mb-10 md:text-xl dark:!text-white/75">
          {{ subtitle }}
        </p>

        <nav :aria-label="t('breadcrumbs.label')">
          <ol class="breadcrumb">
            <li class="breadcrumb-item">
              <LocaleLink to="/">{{ t('breadcrumbs.home') }}</LocaleLink>
            </li>
            <li
              v-for="(item, index) in items"
              :key="`${item.name}-${index}`"
              class="breadcrumb-item"
              :class="{ active: index === items.length - 1 }"
              :aria-current="index === items.length - 1 ? 'page' : undefined"
            >
              <LocaleLink v-if="item.to && index < items.length - 1" :to="item.to">{{ item.name }}</LocaleLink>
              <span v-else class="line-clamp-1 max-w-[60vw]">{{ item.name }}</span>
            </li>
          </ol>
        </nav>
      </div>
    </div>

    <!-- Decorative elements from the template's hero, faded onto the band -->
    <img
      src="/sofax/hero-2-element-1.svg"
      alt=""
      aria-hidden="true"
      width="291"
      height="200"
      class="pointer-events-none absolute -left-10 top-1/2 hidden -translate-y-1/2 opacity-40 dark:opacity-20 xl:inline-block"
    >
    <img
      src="/sofax/hero-2-element-3.svg"
      alt=""
      aria-hidden="true"
      width="311"
      height="243"
      class="pointer-events-none absolute -right-16 bottom-0 hidden opacity-40 dark:opacity-20 xl:inline-block"
    >
  </section>
</template>
